/**
 * The platform's modules — the single place that knows a module's name, its URL prefix and
 * its home route.
 *
 * `key` is load-bearing three times over: it is the URL prefix (`/trace/*`), the value of
 * `panel_type` on a `trace_setups` row, AND the `allow_<key>` column on `account_access`
 * that the backend's `modules[]` is derived from. Keeping them the same string is what makes
 * "which module am I in" a substring check rather than a lookup table.
 *
 * Order here is the order the left nav lists modules, and the first licensed one is where
 * "/" lands.
 *
 * `optIn: true` marks a module whose licence defaults OFF (account_access.allow_<key> = 0).
 * The frontend's fallback module list, used when /api/auth/status can't be read, leaves it
 * out — mirroring MODULE_DEFAULTS in access_control.py.
 */
export const MODULES = [
  {
    key: 'trace',
    name: 'Traceability Matrix',
    prefix: '/trace',
    home: '/trace/dashboard',
    glyph: '⇢',
    blurb: 'Requirement-to-test matrices, coverage funnel, trace graph and the AI trace builder.',
    unit: 'panel',
    unitPlural: 'panels',
  },
  {
    key: 'training',
    name: 'Training Management',
    prefix: '/training',
    home: '/training',
    glyph: '🎓',
    blurb: 'Who owes training on which document revision — and the quizzes that close the gap.',
    unit: 'panel',
    unitPlural: 'panels',
  },
  {
    // OPT-IN (v4.8.0): `account_access.allow_review` defaults to 0. Panels are `trace_setups`
    // rows with panel_type 'review'; reviews live in their own tables (review_db.py).
    key: 'review',
    optIn: true,
    name: 'Document Review',
    prefix: '/review',
    home: '/review',
    glyph: '✓',
    blurb: 'Review DMS drafts with your team, then route them for signature in Orcanos.',
    unit: 'panel',
    unitPlural: 'panels',
  },
  {
    // OPT-IN (v4.12.0): `account_access.allow_risk` defaults to 0. Panels are `trace_setups`
    // rows with panel_type 'risk'; the risks themselves stay in Orcanos (risk_db.py holds
    // settings, baselines, import records and Risk Studio runs).
    key: 'risk',
    optIn: true,
    name: 'Risk Management',
    prefix: '/risk',
    home: '/risk',
    glyph: '△',
    blurb: 'FMEA risk files: how risky the product is, whether every risk is controlled, and what changed.',
    unit: 'panel',
    unitPlural: 'panels',
  },
  {
    // OPT-IN: `account_access.allow_bom` defaults to 0, so this entry appears only for an
    // account an admin has ticked on /admin. It owns no `trace_setups` rows (no panels) —
    // its configuration is the `bom_settings` row, which is why it has no `unit`.
    key: 'bom',
    optIn: true,
    name: 'BOM Viewer',
    prefix: '/bom',
    home: '/bom/boms',
    glyph: '▦',
    blurb: 'Browse Bill-of-Materials trees, where a part is used, costs and revision history.',
  },
  {
    // v4.34.0. NO licence of its own: every account with Traceability has the DHF (the server
    // derives `dhf` from `trace`). A DHF is a Traceability panel linked onto the DHF home
    // (dhf_db.dhf_panels) — it owns no `trace_setups` rows, so no `unit`.
    key: 'dhf',
    name: 'Design History File',
    prefix: '/dhf',
    home: '/dhf',
    glyph: '▤',
    blurb: 'Your design documents scored for completion, coverage and verification — and read like a document.',
  },
  {
    // OPT-IN (v4.25.0): `account_access.allow_integrations` defaults to 0. Owns no panels — its
    // setups are `integrations` rows (integrations_db.py), so it has no `unit`.
    // `navGroup` lists it under its own "Plugins" heading in the left nav, labelled `navLabel`.
    key: 'integrations',
    optIn: true,
    name: 'Integrations',
    navGroup: 'Plugins',
    navLabel: 'Integrations',
    // One nav entry per LICENSED connector (session `connectors`, v4.28.0): each opens the
    // Integrations home filtered to it (`?type=`). AppNav reads this.
    navEntries: [
      { connector: 'jira', label: 'Jira', to: '/integrations?type=jira' },
      { connector: 'github', label: 'GitHub', to: '/integrations?type=github' },
    ],
    prefix: '/integrations',
    home: '/integrations',
    glyph: '⇄',
    blurb: 'Keep Orcanos items and Jira or GitHub issues in step, both ways, with IDs written back.',
  },
];

/** Which module a pathname belongs to, or null on the portal / login / anything else. */
export function moduleOf(pathname) {
  const p = pathname || '';
  const hit = MODULES.find((m) => p === m.prefix || p.startsWith(`${m.prefix}/`));
  return hit ? hit.key : null;
}

export function moduleByKey(key) {
  return MODULES.find((m) => m.key === key) || null;
}
